import { useState } from 'react'

import {
  PublicSpotifyArtist,
  useLazyGetSpotifyArtistByIdQuery,
  useLazySearchSpotifyArtistsQuery,
} from 'src/Redux/publicSpotifyArtistsApi'
import { getIsSpotifyUrl } from 'src/Redux/spotifyTracks-process/utils'

import { getSpotifyArtistId } from './helpers'

const SEARCH_ERROR_MESSAGE = 'Something went wrong. Please try again'

export interface UseSpotifyArtistSearchResult {
  searchValue: string
  searchedArtists: PublicSpotifyArtist[] | null
  searchError: string
  isSearching: boolean
  selectedArtist: PublicSpotifyArtist | null
  trackName: string
  onSearchChange: (value: string) => void
  onArtistSelect: (artist: PublicSpotifyArtist) => void
  onChangeArtist: () => void
  onTrackNameChange: (value: string) => void
}

export const useSpotifyArtistSearch = (): UseSpotifyArtistSearchResult => {
  const [searchValue, setSearchValue] = useState('')
  const [searchedArtists, setSearchedArtists] = useState<
    PublicSpotifyArtist[] | null
  >(null)
  const [searchError, setSearchError] = useState('')
  const [selectedArtist, setSelectedArtist] =
    useState<PublicSpotifyArtist | null>(null)
  const [trackName, setTrackName] = useState('')

  const [searchSpotifyArtists, { isFetching: isSearchFetching }] =
    useLazySearchSpotifyArtistsQuery()
  const [getSpotifyArtistById, { isFetching: isArtistByIdFetching }] =
    useLazyGetSpotifyArtistByIdQuery()

  const onSearchChange = (value: string): void => {
    setSearchValue(value)
    setSearchError('')

    if (!value) {
      setSearchedArtists(null)
      return
    }

    const artistId = getIsSpotifyUrl(value) ? getSpotifyArtistId(value) : ''

    const request = artistId
      ? getSpotifyArtistById(artistId)
          .unwrap()
          .then((artist) => [artist])
      : searchSpotifyArtists({ artistName: value }).unwrap()

    request
      .then((artists) => {
        setSearchedArtists(artists)
      })
      .catch(() => {
        setSearchedArtists(null)
        setSearchError(SEARCH_ERROR_MESSAGE)
      })
  }

  const onArtistSelect = (artist: PublicSpotifyArtist): void => {
    setSelectedArtist(artist)
    setSearchedArtists(null)
    setSearchError('')
  }

  const onChangeArtist = (): void => {
    setSelectedArtist(null)
    setSearchValue('')
    setSearchedArtists(null)
    setSearchError('')
  }

  const onTrackNameChange = (value: string): void => {
    setTrackName(value)
  }

  return {
    isSearching: isSearchFetching || isArtistByIdFetching,
    searchValue,
    searchedArtists,
    searchError,
    selectedArtist,
    trackName,
    onSearchChange,
    onArtistSelect,
    onChangeArtist,
    onTrackNameChange,
  }
}
